import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import type { FiltersMetadata } from '~/modules/artist-roster/types';

interface FilterButtonProps {
  filtersMetadata: FiltersMetadata;
  onShowFilters: (isOpen: boolean) => void;
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1736
 */
// @TODO WMG1-678 - Move to shared location for roster and user management.
export const FilterButton: React.FC<FilterButtonProps> = memo(
  ({ filtersMetadata, onShowFilters }) => {
    const { t } = useTranslation('roster');

    const handleButtonClick = useCallback(() => {
      onShowFilters(true);
    }, [onShowFilters]);

    return (
      <button
        className="relative rounded-sm px-md py-sm leading-8 outline outline-1 duration-300 ease-out bg-emphasis-default outline-emphasis-subtle hover:bg-emphasis-strong hover:outline-emphasis-stronger"
        onClick={handleButtonClick}
        data-testid="artist-filter-button"
        aria-label={t('filters.buttonAriaLabel')}
      >
        <Icon
          type="sliders-simple"
          variant={filtersMetadata.areFiltersApplied ? 'solid' : 'light'}
          size="md"
        />
        {filtersMetadata.areFiltersApplied ? (
          <div
            data-testid="artist-filter-button-badge"
            className="absolute -right-1/4 -top-1/4 grid h-lg min-w-lg items-center rounded-full border p-xs text-caption-md leading-none bg-primary-default border-emphasis-subtle"
          >
            {filtersMetadata.filterCount}
          </div>
        ) : null}
      </button>
    );
  },
);

FilterButton.displayName = 'FilterButton';
